import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthInput } from './AuthInput';
import { ForgotPasswordDialog } from './ForgotPasswordDialog';
import { PasswordRequirements } from './PasswordRequirements';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { passwordRules } from '@/utils/validation/rules';
import { AUTH_CONSTANTS, AUTH_MESSAGES } from './constants';

interface AuthFormProps {
  mode: 'signin' | 'signup';
}

/**
 * Sanitizes auth errors to prevent email enumeration attacks.
 * Returns generic messages that don't reveal whether an email exists.
 */
function sanitizeAuthError(error: Error, mode: 'signin' | 'signup'): string {
  const message = error.message.toLowerCase();

  if (message.includes('rate limit')) {
    return AUTH_MESSAGES.RATE_LIMIT;
  }

  if (message.includes('timeout') || message.includes('504')) {
    return AUTH_MESSAGES.TIMEOUT;
  }

  if (mode === 'signin') {
    // Don't reveal if email exists or not
    if (
      message.includes('invalid') ||
      message.includes('user not found') ||
      message.includes('wrong password') ||
      message.includes('credentials')
    ) {
      return AUTH_MESSAGES.INVALID_CREDENTIALS;
    }
  }

  if (mode === 'signup') {
    // Don't reveal if email already exists
    if (
      message.includes('already registered') ||
      message.includes('already exists') ||
      message.includes('duplicate')
    ) {
      return AUTH_MESSAGES.SIGNUP_FAILED;
    }
  }

  return AUTH_MESSAGES.GENERIC_ERROR;
}

export function AuthForm({ mode }: AuthFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signIn, signUp } = useAuth();
  const { toast } = useToast();

  // Cooldown countdown effect
  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsLoading(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate password on signup
    if (mode === 'signup') {
      const validation = passwordRules.validateAll(password);
      if (!validation.isValid) {
        setError(validation.errors[0]);
        toast({
          title: 'Password Requirements',
          description: validation.errors[0],
          variant: 'destructive',
        });
        return;
      }
    }

    setIsLoading(true);

    try {
      if (mode === 'signup') {
        await signUp(email, password);
        toast({
          title: 'Success',
          description: AUTH_MESSAGES.SIGNUP_SUCCESS,
        });
      } else {
        await signIn(email, password);
      }
    } catch (err) {
      const authError = err instanceof Error ? err : new Error('Authentication failed');
      const errorMessage = sanitizeAuthError(authError, mode);

      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });

      // Start cooldown on rate limit
      if (authError.message.toLowerCase().includes('rate limit')) {
        setCooldown(AUTH_CONSTANTS.RATE_LIMIT_COOLDOWN_SECONDS);
      } else {
        setIsLoading(false);
      }
    }
  };

  const isDisabled = isLoading || cooldown > 0;

  return (
    <div className="space-y-4">
      <form
        onSubmit={handleSubmit}
        className="space-y-4"
        aria-label={mode === 'signin' ? 'Sign in form' : 'Sign up form'}
      >
        {/* Screen reader error announcements */}
        {error && (
          <div role="alert" aria-live="assertive" className="sr-only">
            {error}
          </div>
        )}

        <AuthInput
          id="email"
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="Enter your email"
          autoComplete={mode === 'signup' ? 'email' : 'email'}
          disabled={isDisabled}
        />

        <div className="space-y-1">
          <AuthInput
            id="password"
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder="Enter your password"
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            disabled={isDisabled}
            showPasswordToggle
            ariaDescribedBy={mode === 'signup' ? 'password-requirements' : undefined}
          />
          {mode === 'signin' && (
            <Button
              type="button"
              variant="link"
              className="px-0 text-sm"
              onClick={() => setShowForgotPassword(true)}
              disabled={isDisabled}
            >
              Forgot password?
            </Button>
          )}
        </div>

        {mode === 'signup' && (
          <div id="password-requirements">
            <PasswordRequirements password={password} />
          </div>
        )}

        <Button type="submit" className="w-full" disabled={isDisabled}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {cooldown > 0
            ? `Wait ${cooldown}s`
            : mode === 'signup'
            ? 'Sign Up'
            : 'Sign In'}
        </Button>
      </form>

      <ForgotPasswordDialog
        open={showForgotPassword}
        onOpenChange={setShowForgotPassword}
      />
    </div>
  );
}
